/**
 * Error Codes Constants
 * Centralized error code definitions for token-related operations
 * These codes should be used for programmatic error handling instead of error message strings
 */

// Login-related error codes
export const LOGIN_ERROR_CODES = {
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
    ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
    ACCOUNT_ARCHIVED: 'ACCOUNT_ARCHIVED',
    PASSWORD_NOT_SET: 'PASSWORD_NOT_SET',
    INVALID_EMAIL_FORMAT: 'INVALID_EMAIL_FORMAT',
    MEMBER_INACTIVE: 'MEMBER_INACTIVE',
}

// Refresh token-related error codes
export const REFRESH_TOKEN_ERROR_CODES = {
    INVALID_REFRESH_TOKEN: 'INVALID_REFRESH_TOKEN',
    REFRESH_TOKEN_EXPIRED: 'REFRESH_TOKEN_EXPIRED',
    REFRESH_TOKEN_REVOKED: 'REFRESH_TOKEN_REVOKED',
    REFRESH_TOKEN_NOT_FOUND: 'REFRESH_TOKEN_NOT_FOUND',
    TOKEN_REUSE_DETECTED: 'TOKEN_REUSE_DETECTED',
    MEMBER_NOT_FOUND: 'MEMBER_NOT_FOUND',
}

// Revoke token-related error codes
export const REVOKE_TOKEN_ERROR_CODES = {
    INVALID_REFRESH_TOKEN: 'INVALID_REFRESH_TOKEN',
    REFRESH_TOKEN_NOT_FOUND: 'REFRESH_TOKEN_NOT_FOUND',
}

// System error codes
export const SYSTEM_ERROR_CODES = {
    INTERNAL_ERROR: 'INTERNAL_ERROR',
}

// All error codes combined
export const ERROR_CODES = {
    ...LOGIN_ERROR_CODES,
    ...REFRESH_TOKEN_ERROR_CODES,
    ...REVOKE_TOKEN_ERROR_CODES,
    ...SYSTEM_ERROR_CODES,
}

/**
 * Check if error code is a token-related error
 * @param {string} errorCode - Error code to check
 * @returns {boolean} Whether the error is token-related
 */
export const isTokenError = errorCode => {
    return (
        Object.values(REFRESH_TOKEN_ERROR_CODES).includes(errorCode) ||
        Object.values(REVOKE_TOKEN_ERROR_CODES).includes(errorCode)
    )
}

/**
 * Check if error code indicates a security threat
 * @param {string} errorCode - Error code to check
 * @returns {boolean} Whether the error indicates a security threat
 */
export const isSecurityThreat = errorCode => {
    return errorCode === REFRESH_TOKEN_ERROR_CODES.TOKEN_REUSE_DETECTED
}

/**
 * Check if error code should trigger navigation to login page
 * @param {string} errorCode - Error code to check
 * @returns {boolean} Whether should navigate to login
 */
export const shouldNavigateToLogin = errorCode => {
    return (
        isTokenError(errorCode) ||
        errorCode === LOGIN_ERROR_CODES.INVALID_CREDENTIALS ||
        errorCode === LOGIN_ERROR_CODES.MEMBER_INACTIVE
    )
}

/**
 * Check if error code should show security alert
 * @param {string} errorCode - Error code to check
 * @returns {boolean} Whether should show security alert
 */
export const shouldShowSecurityAlert = errorCode => {
    return isSecurityThreat(errorCode)
}

/**
 * Check if error code should navigate to email verification
 * @param {string} errorCode - Error code to check
 * @returns {boolean} Whether should navigate to email verification
 */
export const shouldNavigateToEmailVerification = errorCode => {
    return errorCode === LOGIN_ERROR_CODES.EMAIL_NOT_VERIFIED
}

/**
 * Check if error code should navigate to password reset
 * @param {string} errorCode - Error code to check
 * @returns {boolean} Whether should navigate to password reset
 */
export const shouldNavigateToPasswordReset = errorCode => {
    return errorCode === LOGIN_ERROR_CODES.PASSWORD_NOT_SET
}

/**
 * Check if error code indicates account status issue
 * @param {string} errorCode - Error code to check
 * @returns {boolean} Whether indicates account status issue
 */
export const isAccountStatusError = errorCode => {
    return errorCode === LOGIN_ERROR_CODES.ACCOUNT_SUSPENDED || errorCode === LOGIN_ERROR_CODES.ACCOUNT_ARCHIVED
}
